import { useState, useEffect } from 'react';
import {
  Search,
  FileText,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  ExternalLink,
} from 'lucide-react';

interface ProviderApproval {
  id: string;
  email: string;
  company_name: string | null;
  ceo_name: string | null;
  business_category: string | null;
  business_certificate_url: string | null;
  business_certificate_uploaded_at: string | null;
  business_approved: boolean;
  business_approved_at: string | null;
  created_at: string;
}

type Filter = 'all' | 'pending' | 'approved';

export default function AdminProviderApprovalTab() {
  const [providers, setProviders] = useState<ProviderApproval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    provider: ProviderApproval;
    newStatus: boolean;
  } | null>(null);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  const fetchProviders = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
        filter,
        ...(activeSearch && { search: activeSearch }),
      });
      const response = await fetch(`/api/v1/admin/provider-approvals?${params}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('교육 기관 목록을 불러오는데 실패했습니다.');
      }
      const data = await response.json();
      setProviders(data.providers || []);
      setTotalPages(data.totalPages || 1);
      setTotalCount(data.totalCount || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : '오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, activeSearch, filter]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setActiveSearch(searchTerm);
  };

  const handleShowAll = () => {
    setSearchTerm('');
    setActiveSearch('');
    setPage(1);
  };

  const handleToggleApproval = (provider: ProviderApproval) => {
    setConfirmModal({ provider, newStatus: !provider.business_approved });
  };

  const confirmToggle = async () => {
    if (!confirmModal) return;
    const { provider, newStatus } = confirmModal;
    setUpdatingId(provider.id);
    setMessage(null);
    setConfirmModal(null);

    try {
      const response = await fetch(`/api/v1/admin/provider-approvals/${provider.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ approved: newStatus }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || '승인 상태 변경에 실패했습니다.');
      }
      setMessage({ type: 'success', text: data.message });
      await fetchProviders();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : '오류가 발생했습니다.',
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const pendingCount = providers.filter((p) => !p.business_approved).length;
  const approvedCount = providers.filter((p) => p.business_approved).length;

  return (
    <div>
      {/* 헤더 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FileText className="w-6 h-6 text-accent" />
          교육 기관 검토
        </h1>
        <p className="text-gray-500 mt-1">
          전체 {totalCount}개 / 현재 페이지 — 대기 {pendingCount}, 승인 {approvedCount}
        </p>
      </div>

      {/* 메시지 */}
      {message && (
        <div
          className={`mb-4 p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* 필터 + 검색 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="flex gap-2">
          {(['all', 'pending', 'approved'] as Filter[]).map((f) => (
            <button
              key={f}
              onClick={() => {
                setFilter(f);
                setPage(1);
              }}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === f
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f === 'all' ? '전체' : f === 'pending' ? '승인 대기' : '승인 완료'}
            </button>
          ))}
        </div>

        <form onSubmit={handleSearch} className="flex-1 min-w-[200px] flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="업체명·대표자·이메일 검색"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90"
          >
            검색
          </button>
          {(activeSearch || filter !== 'all') && (
            <button
              type="button"
              onClick={handleShowAll}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
            >
              전체 보기
            </button>
          )}
        </form>
      </div>

      {/* 본문 */}
      {loading ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center text-gray-500">
          불러오는 중...
        </div>
      ) : error ? (
        <div className="bg-red-50 text-red-700 rounded-xl border border-red-200 p-6">{error}</div>
      ) : providers.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center text-gray-500">
          조건에 맞는 교육 기관이 없습니다.
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">업체명</th>
                  <th className="px-4 py-3 text-left font-medium">대표자</th>
                  <th className="px-4 py-3 text-left font-medium">이메일</th>
                  <th className="px-4 py-3 text-left font-medium">업종</th>
                  <th className="px-4 py-3 text-left font-medium">제출 파일</th>
                  <th className="px-4 py-3 text-left font-medium">제출일</th>
                  <th className="px-4 py-3 text-center font-medium">승인</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {providers.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {p.company_name || '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{p.ceo_name || '-'}</td>
                    <td className="px-4 py-3 text-gray-700">{p.email}</td>
                    <td className="px-4 py-3 text-gray-700">{p.business_category || '-'}</td>
                    <td className="px-4 py-3">
                      {p.business_certificate_url ? (
                        <a
                          href={p.business_certificate_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-accent hover:underline"
                        >
                          파일 열기
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <span className="text-gray-400">미제출</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {formatDate(p.business_certificate_uploaded_at)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleToggleApproval(p)}
                        disabled={updatingId === p.id || !p.business_certificate_url}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          p.business_approved ? 'bg-green-500' : 'bg-gray-200'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                        title={
                          p.business_certificate_url
                            ? p.business_approved
                              ? '승인됨 (클릭하여 해제)'
                              : '미승인 (클릭하여 승인)'
                            : '제출된 파일이 없어 승인할 수 없습니다'
                        }
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                            p.business_approved ? 'translate-x-5' : 'translate-x-0.5'
                          }`}
                        />
                      </button>
                      <div className="text-xs text-gray-500 mt-1">
                        {p.business_approved ? formatDate(p.business_approved_at) : '대기'}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <span className="text-sm text-gray-500">
                {page} / {totalPages} 페이지 (총 {totalCount}개)
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-1.5 rounded-md border border-gray-200 text-gray-600 disabled:opacity-40 hover:bg-gray-100"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-1.5 rounded-md border border-gray-200 text-gray-600 disabled:opacity-40 hover:bg-gray-100"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 확인 모달 */}
      {confirmModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle
                className={`w-6 h-6 ${
                  confirmModal.newStatus ? 'text-green-500' : 'text-amber-500'
                }`}
              />
              <h3 className="text-lg font-semibold text-gray-900">
                {confirmModal.newStatus ? '승인하시겠습니까?' : '승인을 해제하시겠습니까?'}
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              <strong>{confirmModal.provider.company_name || confirmModal.provider.email}</strong>
            </p>
            <p className="text-sm text-gray-500 mb-6">
              {confirmModal.newStatus
                ? '승인 후 해당 교육 기관은 즉시 수업을 등록할 수 있습니다.'
                : '승인을 해제하면 해당 교육 기관은 더 이상 새 수업을 등록할 수 없습니다.'}
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmModal(null)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
              >
                취소
              </button>
              <button
                onClick={confirmToggle}
                className={`px-4 py-2 rounded-lg text-sm font-medium text-white ${
                  confirmModal.newStatus
                    ? 'bg-green-500 hover:bg-green-600'
                    : 'bg-amber-500 hover:bg-amber-600'
                }`}
              >
                {confirmModal.newStatus ? '승인' : '해제'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
